import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { projectAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  Building2, 
  Plus,
  ArrowRight,
  Loader2,
  Search,
  Filter,
  MoreVertical,
  ChevronRight,
  Home,
  DollarSign,
  TrendingUp,
  Calendar,
  MapPin,
  GripVertical
} from 'lucide-react';
import { toast } from 'sonner';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value || 0);
};

// Pipeline stages configuration
const PIPELINE_STAGES = [
  { 
    id: 'lead', 
    name: 'Lead', 
    color: 'from-slate-500/20 to-slate-600/20',
    borderColor: 'border-slate-500/30',
    textColor: 'text-slate-400',
    description: 'New opportunities'
  },
  { 
    id: 'analysis', 
    name: 'Analysis', 
    color: 'from-blue-500/20 to-blue-600/20',
    borderColor: 'border-blue-500/30',
    textColor: 'text-blue-400',
    description: 'Under evaluation'
  },
  { 
    id: 'offer', 
    name: 'Offer', 
    color: 'from-amber-500/20 to-amber-600/20',
    borderColor: 'border-amber-500/30',
    textColor: 'text-amber-400',
    description: 'Offer submitted'
  },
  { 
    id: 'contract', 
    name: 'Under Contract', 
    color: 'from-purple-500/20 to-purple-600/20',
    borderColor: 'border-purple-500/30',
    textColor: 'text-purple-400',
    description: 'Deal secured'
  },
  { 
    id: 'rehab', 
    name: 'Rehab', 
    color: 'from-orange-500/20 to-orange-600/20',
    borderColor: 'border-orange-500/30',
    textColor: 'text-orange-400',
    description: 'In renovation'
  },
  { 
    id: 'sold', 
    name: 'Sold', 
    color: 'from-green-500/20 to-green-600/20',
    borderColor: 'border-green-500/30',
    textColor: 'text-green-400',
    description: 'Completed deals'
  }
];

// Map existing status to pipeline stage
const statusToStage = (status) => {
  const mapping = {
    'planning': 'lead',
    'in-progress': 'rehab',
    'completed': 'sold',
    'sold': 'sold'
  };
  return mapping[status] || 'lead';
};

// Normalize project data from API
const normalizeProject = (project) => ({
  ...project,
  arv: project.arv || project.estimated_arv || 0,
  rehab_budget: project.rehab_budget || project.target_budget || 0,
  pipeline_stage: project.pipeline_stage || statusToStage(project.status)
});

const PipelinePage = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draggedProject, setDraggedProject] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await projectAPI.getAll();
      // Normalize and add pipeline_stage to each project
      const projectsWithStage = (response.data || []).map(normalizeProject);
      setProjects(projectsWithStage);
    } catch (error) {
      console.error('Failed to load projects:', error);
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = (e, project) => {
    setDraggedProject(project);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, stageId) => {
    e.preventDefault();
    if (!draggedProject || draggedProject.pipeline_stage === stageId) {
      setDraggedProject(null);
      return;
    }

    // Optimistically update UI
    setProjects(prev => prev.map(p => 
      p.id === draggedProject.id ? { ...p, pipeline_stage: stageId } : p
    ));

    // Update on server
    try {
      await projectAPI.update(draggedProject.id, { pipeline_stage: stageId });
      toast.success(`Moved to ${PIPELINE_STAGES.find(s => s.id === stageId)?.name}`);
    } catch (error) {
      // Revert on error
      fetchProjects();
      toast.error('Failed to update project stage');
    }

    setDraggedProject(null);
  };

  const getProjectsByStage = (stageId) => {
    return projects.filter(p => p.pipeline_stage === stageId);
  };

  const filteredProjects = searchTerm 
    ? projects.filter(p => 
        p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.address?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : projects;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading pipeline...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="pipeline-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-extrabold font-heading tracking-tight">Deal Pipeline</h1>
          <p className="text-muted-foreground mt-1">
            Track your deals from lead to sold • Drag & drop to update status
          </p>
        </div>
        <div className="flex gap-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search deals..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 pr-4 py-2 text-sm bg-secondary/50 border border-border rounded-sm focus:outline-none focus:border-primary/50 w-48"
            />
          </div>
          <Button 
            onClick={() => navigate('/projects/new')}
            className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm btn-scale"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Deal
          </Button>
        </div>
      </div>

      {/* Pipeline Stats */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
        {PIPELINE_STAGES.map(stage => {
          const count = getProjectsByStage(stage.id).length;
          const value = getProjectsByStage(stage.id).reduce((sum, p) => sum + (p.arv || 0), 0);
          return (
            <div 
              key={stage.id} 
              className={`p-3 rounded-sm border ${stage.borderColor} bg-gradient-to-br ${stage.color}`}
            >
              <p className={`text-xs font-medium ${stage.textColor}`}>{stage.name}</p>
              <p className="text-2xl font-bold font-mono mt-1">{count}</p>
              <p className="text-xs text-muted-foreground">{formatCurrency(value)}</p>
            </div>
          );
        })}
      </div>

      {/* Kanban Board */}
      <div className="overflow-x-auto pb-4">
        <div className="flex gap-4 min-w-max">
          {PIPELINE_STAGES.map(stage => {
            const stageProjects = searchTerm 
              ? filteredProjects.filter(p => p.pipeline_stage === stage.id)
              : getProjectsByStage(stage.id);
            
            return (
              <div
                key={stage.id}
                className={`w-72 flex-shrink-0 rounded-sm border ${stage.borderColor} bg-gradient-to-b ${stage.color} backdrop-blur-sm`}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, stage.id)}
                data-testid={`pipeline-stage-${stage.id}`}
              >
                {/* Stage Header */}
                <div className="p-4 border-b border-border/50">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className={`font-semibold ${stage.textColor}`}>{stage.name}</h3>
                      <p className="text-xs text-muted-foreground">{stage.description}</p>
                    </div>
                    <Badge variant="outline" className={`${stage.textColor} border-current/30 font-mono`}>
                      {stageProjects.length}
                    </Badge>
                  </div>
                </div>

                {/* Stage Content */}
                <div className="p-3 space-y-3 min-h-[300px] max-h-[500px] overflow-y-auto">
                  {stageProjects.map((project) => (
                    <div
                      key={project.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, project)}
                      className={`group bg-card/80 backdrop-blur border border-border rounded-sm p-3 cursor-grab active:cursor-grabbing hover:border-primary/50 transition-all hover:shadow-lg ${
                        draggedProject?.id === project.id ? 'opacity-50 scale-95' : ''
                      }`}
                      data-testid={`pipeline-project-${project.id}`}
                    >
                      <div className="flex items-start gap-2">
                        <GripVertical className="w-4 h-4 text-muted-foreground/50 mt-0.5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                        <div className="flex-1 min-w-0">
                          <Link 
                            to={`/projects/${project.id}`}
                            className="font-semibold text-sm hover:text-primary transition-colors line-clamp-1"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {project.name}
                          </Link>
                          <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                            <MapPin className="w-3 h-3" />
                            {project.city}, {project.state}
                          </p>
                          
                          <div className="flex items-center gap-3 mt-3">
                            <div className="flex items-center gap-1">
                              <DollarSign className="w-3 h-3 text-muted-foreground" />
                              <span className="text-xs font-mono">{formatCurrency(project.purchase_price)}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <TrendingUp className="w-3 h-3 text-green-400" />
                              <span className="text-xs font-mono text-green-400">{formatCurrency(project.arv)}</span>
                            </div>
                          </div>

                          {project.rehab_budget > 0 && (
                            <div className="mt-2 pt-2 border-t border-border/50">
                              <div className="flex items-center justify-between text-xs">
                                <span className="text-muted-foreground">Potential Profit</span>
                                <span className="font-mono text-green-400">
                                  {formatCurrency((project.arv || 0) - (project.purchase_price || 0) - (project.rehab_budget || 0))}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}

                  {stageProjects.length === 0 && (
                    <div className="flex flex-col items-center justify-center py-8 text-center">
                      <div className={`w-10 h-10 rounded-full ${stage.color} flex items-center justify-center mb-2`}>
                        <Building2 className={`w-5 h-5 ${stage.textColor} opacity-50`} />
                      </div>
                      <p className="text-xs text-muted-foreground">No deals in {stage.name.toLowerCase()}</p>
                      {stage.id === 'lead' && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => navigate('/projects/new')}
                          className="mt-2 text-xs"
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          Add first deal
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 pt-4 border-t border-border">
        <span className="text-xs text-muted-foreground">Pipeline stages:</span>
        {PIPELINE_STAGES.map(stage => (
          <div key={stage.id} className="flex items-center gap-1.5">
            <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${stage.color}`}></div>
            <span className="text-xs text-muted-foreground">{stage.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PipelinePage;
